import { Injectable } from '@angular/core';
import { IUserPreferences, UsersApiService } from '@epicuro-next/data/users';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  deleteAssortmentCode,
  postUserPreferences,
  postUserPreferencesFailure,
  postUserPreferencesSuccess,
  setPreferredScaleSet,
  submitAssortmentCode,
} from '../actions';
import {
  selectAssortmentList,
  selectPreferences,
} from '../user-state.selectors';

@Injectable()
export class UserPreferencesEffects {
  constructor(
    private actions$: Actions,
    private userService: UsersApiService,
    private store: Store,
  ) {}

  public setPreferredScaleSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPreferredScaleSet),
      map(({ scaleSetId }) =>
        postUserPreferences({
          preferences: { preferredScaleSet: scaleSetId } as IUserPreferences,
        }),
      ),
    ),
  );

  public updateAssortments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitAssortmentCode, deleteAssortmentCode),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(selectAssortmentList), take(1)),
          ),
        ),
      ),
      map(([action, assortments]) => {
        let relatedProductsFilterList: string[];

        if (action.type === submitAssortmentCode.type) {
          relatedProductsFilterList = [...assortments, action.assortmentCode];
        } else {
          relatedProductsFilterList = assortments.filter(
            (_, assortmentIndex) =>
              assortmentIndex !== action.assortmentCodeIndex,
          );
        }

        return postUserPreferences({
          preferences: { relatedProductsFilterList },
        });
      }),
    ),
  );

  public postUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postUserPreferences),
      withLatestFrom(
        this.store.pipe(
          select(selectPreferences),
          map((preferences) => preferences ?? ({} as IUserPreferences)),
        ),
      ),
      // current and new preferences are merged to avoid removing other preferences
      map(([{ preferences }, currentPreferences]) => ({
        ...currentPreferences,
        ...preferences,
      })),
      switchMap((preferences) =>
        this.userService.postPreferences(preferences).pipe(
          map(() => postUserPreferencesSuccess({ preferences })),
          catchError((error) => of(postUserPreferencesFailure({ error }))),
        ),
      ),
    ),
  );
}
